import React, { Dispatch, SetStateAction, useContext, useState, useEffect } from 'react';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import Paper from '@material-ui/core/Paper';
import { useParams } from 'react-router-dom';
import InputBase from '@material-ui/core/InputBase';
import { createStyles, Theme, makeStyles } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';
import { MeetingContext } from '../../App';
import { useAppState } from '../../state';
import useVideoContext from '../../hooks/useVideoContext/useVideoContext';
import EntryMediaSwitch from '../EntryMediaSwitch/EntryMediaSwitch';

interface IEntryModal {
  setName?: Dispatch<SetStateAction<string>>;
  joinRoom?: (userName: string) => void;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    title: {
      fontWeight: 500,
      fontSize: '1.3rem',
      margin: '15px',
    },
    root: {
      width: '800',
    },
    continue: {
      right: 0,
      backgroundColor: '#1e88e5',
      color: 'white',
      '&:hover': {
        backgroundColor: '#1e88e5',
      },
    },
    input: {
      marginLeft: theme.spacing(1),
      flex: 1,
    },
    paper: {
      width: '100%',
      display: 'flex',
      alignItems: 'center',
    },
    caption: {
      display: 'flex',
      justifyContent: 'center',
    },
    terms: {
      textDecorationLine: 'underline',
      textDecorationColor: '#1e88e5',
    },
    error: {
      display: 'flex',
      justifyContent: 'center',
      color: '#d50000',
      margin: '2px',
    },
    valid: {
      display: 'flex',
      justifyContent: 'center',
      color: 'transparent',
      margin: '2px',
    },
    buttonContainer: {
      display: 'flex',
      justifyContent: 'center',
    },
    titleContainer: {
      display: 'flex',
      justifyContent: 'center',
    },
    border: {
      right: 0,
      backgroundColor: '#1e88e5',
      color: 'white',
      '&:hover': {
        backgroundColor: '#1e88e5',
      },
      margin: '10px',
      borderRadius: '16px',
    },
    switch: {
      display: 'flex',
      justifyContent: 'center',
    },
  })
);

export default function EntryDialog() {
  const { company, userType, isMobile, meeting, validLink } = useContext(MeetingContext);
  const user = userType === 'guest' ? JSON.parse(localStorage.getItem('guest_name')) || '' : '';
  const [userName, setUserName] = React.useState(user);
  const classes = useStyles();
  const queryStr = window.location.search;
  const query = new URLSearchParams(queryStr);
  const { URLRoomName, URLParticipantName } = useParams();
  const [nameError, setNameError] = useState(false);
  const { getToken } = useAppState();
  const [roomName, setRoomName] = useState<string>('');
  const [hostName, setHostName] = useState<string>(query.get('participant_name'));
  const [recievedMeetingDetails, setRecievedMeetingDetails] = useState(null);
  const [buttonClicked, setButtonClicked] = useState(false);
  const { connect } = useVideoContext();
  const title = userType === 'guest' ? 'Welcome to' : 'Start';
  const errorText = 'Name should be 3 to 25 characters in length';

  useEffect(() => {
    if (meeting?.meeting_status === 'OPEN') setRecievedMeetingDetails(true);
  }, [meeting, validLink]);

  useEffect(() => {
    if (URLRoomName) {
      setRoomName(URLRoomName);
    }
    if (!URLParticipantName) {
    }
    if (hostName) joinRoom(hostName);
  }, [URLRoomName, URLParticipantName]);

  const handleChange = name => {
    !(name.length > 2 && name.length < 26) ? setNameError(true) : setNameError(false);
    setUserName(name);
  };

  const handleClose = event => {
    setButtonClicked(true);
    event.preventDefault();
    if (userType === 'guest') localStorage.setItem('guest_name', JSON.stringify(userName));
    joinRoom(userName);
  };

  const joinRoom = (userName: string) => {
    // If this app is deployed as a twilio function, don't change the URL beacuse routing isn't supported.
    if (userType === 'host') {
      sessionStorage.setItem('token', JSON.stringify(query.get('token')));
      userName = userName + ' (Host)';
    }
    if (!window.location.origin.includes('twil.io')) {
      window.history.replaceState(null, '', window.encodeURI(`/room/${roomName}${window.location.search || ''}`));
    }
    getToken(userName, roomName).then(token => {
      connect(token, userType);
    });
  };

  return !isMobile ? (
    <>
      <form onSubmit={handleClose}>
        <div className={classes.titleContainer}>
          <Typography className={classes.title}>{company ? company.company_name : ' '} Remote Session </Typography>
        </div>
        <Paper variant="outlined" className={classes.paper}>
          <InputBase
            autoFocus
            value={userName}
            onChange={e => handleChange(e.target.value)}
            className={classes.input}
            placeholder={'Your Name'}
          />
        </Paper>
        <Typography variant="caption" className={nameError ? classes.error : classes.valid}>
          {errorText}
        </Typography>
        <Typography variant="caption">
          By Joining this call, you agree to our{' '}
          <Typography className={classes.terms} variant="caption">
            terms and conditions{' '}
          </Typography>
        </Typography>
        <div className={classes.buttonContainer}>
          <Button
            disabled={nameError || userName === '' || buttonClicked}
            type="submit"
            className={classes.border}
            variant="contained"
            onClick={e => handleClose(e)}
          >
            JOIN NOW
          </Button>
        </div>
      </form>
    </>
  ) : (
    <>
      {recievedMeetingDetails && (
        <Dialog
          disableBackdropClick
          disableEscapeKeyDown
          className={classes.root}
          open={true}
          fullWidth={true}
          maxWidth={'sm'}
        >
          <DialogTitle>
            {title} {company ? company.company_name : ' '} Remote Session{' '}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>Your Name</DialogContentText>
            <Paper component="form" variant="outlined" className={classes.paper}>
              <InputBase
                autoFocus
                value={userName}
                onChange={e => handleChange(e.target.value)}
                className={classes.input}
              />
              <Button
                disabled={nameError || userName === '' || buttonClicked}
                type="submit"
                className={classes.continue}
                variant="contained"
                onClick={e => handleClose(e)}
              >
                JOIN ROOM
              </Button>
            </Paper>
            <Typography variant="caption" className={nameError ? classes.error : classes.valid}>
              {errorText}
            </Typography>
            <DialogContentText className={classes.caption}>
              <Typography variant="caption">
                By Joining this call, you agree to our{' '}
                <Typography className={classes.terms} variant="caption">
                  terms and conditions{' '}
                </Typography>
              </Typography>
            </DialogContentText>
          </DialogContent>
          <DialogContent className={classes.switch}>
            <EntryMediaSwitch />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
